import { Initiative } from '../services/apiService';
import { getTeamFromSprint } from '../services/teamMapping';
import { calculateProgressPercentage } from './progressCalculations';

export interface ProcessedInitiative {
  id: string;
  epicRank: string;
  epicRankNum: number;
  key: string;
  summary: string;
  theme: string;
  sprint: string;
  teamName: string;
  status: string;
  statusCategory: string;
  statusColor: string;
  assignee: string;
  assigneeEmail: string;
  assigneeAvatar: string;
  reporter: string;
  priority: string;
  priorityIcon: string;
  created: string;
  updated: string;
  description: string;
  startedAtSprint: string;
  endsAtSprint: string;
  startDate: string | null;
  endDate: string | null;
  missingStartSprint: boolean;
  missingEndSprint: boolean;
  missingSprintData: boolean;
  url: string;
  labels: string[]; // Add labels field for filtering
  customFields: {
    orderByField: any;
    startedAtSprint: any;
    endsAtSprint: any;
  };
  // Story points and progress tracking
  totalStoryPoints?: number;
  completedStoryPoints?: number;
  progressPercentage?: number;
  // Team enrichment data
  teams?: string[];
  team?: string;
  teamSource?: string;
  childEpicTeams?: string[];
  epics?: any[];
}

/**
 * Process timeline data for initiatives that have timeline API data
 */
export function processTimelineData<T extends { key: string, timeline?: any }>(initiatives: T[]): T[] {
  return initiatives.map((initiative, index) => {
    if (initiative.timeline) {


      // Add timeline dates to the initiative object for easy access
      // IMPORTANT: Only use timeline API dates if they exist, otherwise preserve existing dates
      return {
        ...initiative,
        startDate: initiative.timeline.startDate || initiative.startDate,
        endDate: initiative.timeline.endDate || initiative.endDate,
        hasCompleteTimeline: initiative.timeline.hasCompleteTimeline
      }
    }
    return initiative
  })
}

/**
 * Process raw Jira initiatives into structured data for the dashboard
 */
export function processInitiatives(initiatives: Initiative[], skipTimelineProcessing = false): ProcessedInitiative[] {

  return initiatives.map((initiative, index) => {
    const sprintName = extractSprintFromCustomFields(initiative);
    const teamName = getTeamFromSprint(sprintName);
    const startedAtSprint = extractStartedAtSprint(initiative);
    const endsAtSprint = extractEndsAtSprint(initiative);
    
    // TIMELINE DATA: Use timeline API data if available, otherwise fall back to legacy logic
    let startDate = null
    let endDate = null

    if (skipTimelineProcessing) {
      // Skip timeline processing - Timeline API will handle this later
      if (index < 3) {
      }
    } else {
      // Check if timeline data is available (will be added by timeline API)
      if ((initiative as any).timeline) {
        const timeline = (initiative as any).timeline
        startDate = timeline.startDate
        endDate = timeline.endDate

        // Debug timeline API data for first few initiatives and RED-151656 specifically
        if (index < 3 || initiative.key === 'RED-151656') {
          if (initiative.key === 'RED-151656') {
          }
        }
      } else {
        // LEGACY FALLBACK: Only use timeline dates when actual JIRA custom fields are populated
        // Only calculate timeline if BOTH custom fields have real sprint numbers
        if (startedAtSprint && endsAtSprint &&
            startedAtSprint.trim() !== '' && endsAtSprint.trim() !== '') {

          // Parse sprint numbers
          const startSprintNum = parseInt(startedAtSprint, 10)
          const endSprintNum = parseInt(endsAtSprint, 10)

          // Only proceed if both are valid numbers
          if (!isNaN(startSprintNum) && !isNaN(endSprintNum)) {
            const sprintDates = calculateSprintDates(startSprintNum, endSprintNum)
            startDate = sprintDates.startDate
            endDate = sprintDates.endDate
          }
        }

        // Debug legacy timeline data for first few initiatives
        if (index < 3) {
        }
      }
    }

    // PHASE 2 FIX: Ensure dates from customFields are available at top level
    // This fixes the issue where dates exist in customFields but not at the top level
    // Priority: customFields dates > calculated dates > null
    if (initiative.customFields?.startDate && !startDate) {
      startDate = initiative.customFields.startDate
    }
    if (initiative.customFields?.endDate && !endDate) {
      endDate = initiative.customFields.endDate
    }
    
    const missingStartSprint = !startedAtSprint || startedAtSprint.trim() === '';
    const missingEndSprint = !endsAtSprint || endsAtSprint.trim() === '';
    
    return {
      id: initiative.id,
      epicRank: extractEpicRank(initiative),
      epicRankNum: parseEpicRankNumber(extractEpicRank(initiative)),
      key: initiative.key || '',
      summary: initiative.summary || '',
      theme: extractTheme(initiative),
      sprint: sprintName,
      teamName: teamName,
      status: initiative.status?.name || '',
      statusCategory: initiative.status?.category || '',
      statusColor: initiative.status?.colorName || '',
      assignee: initiative.assignee?.displayName || '',
      assigneeEmail: initiative.assignee?.emailAddress || '',
      assigneeAvatar: initiative.assignee?.avatarUrls?.['24x24'] || '',
      reporter: initiative.reporter?.displayName || '',
      priority: initiative.priority?.name || '',
      priorityIcon: initiative.priority?.iconUrl || '',
      created: initiative.created,
      updated: initiative.updated,
      description: extractDescription(initiative.description),
      startedAtSprint: startedAtSprint,
      endsAtSprint: endsAtSprint,
      startDate: startDate,
      endDate: endDate,
      missingStartSprint: missingStartSprint,
      missingEndSprint: missingEndSprint,
      missingSprintData: missingStartSprint || missingEndSprint,
      url: initiative.url,
      labels: initiative.labels || [], // Include labels for filtering
      customFields: initiative.customFields,
      // Preserve story points for risk calculation
      totalStoryPoints: initiative.totalStoryPoints || 0,
      completedStoryPoints: initiative.completedStoryPoints || 0,
      // Calculate progress percentage
      progressPercentage: calculateProgressPercentage(
        initiative.completedStoryPoints || 0,
        initiative.totalStoryPoints || 0,
        1 // 1 decimal place precision
      ),
      // Preserve team enrichment data
      teams: initiative.teams || [],
      team: initiative.team,
      teamSource: initiative.teamSource,
      childEpicTeams: initiative.childEpicTeams || [],
      epics: initiative.epics || []
    };
  }).sort((a, b) => a.epicRankNum - b.epicRankNum);
}

/**
 * Extract epic rank from initiative (from custom field or other source)
 */
function extractEpicRank(initiative: Initiative): string {
  // Try to get from custom fields first
  if (initiative.customFields.orderByField !== null && initiative.customFields.orderByField !== undefined) {
    return String(initiative.customFields.orderByField);
  }

  // Fallback to a default
  return '999';
}

/**
 * Parse epic rank number for sorting
 */
function parseEpicRankNumber(value: string): number {
  if (value === '0') return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 999 : parsed;
}

/**
 * Extract sprint name from custom fields
 */
function extractSprintFromCustomFields(initiative: Initiative): string {
  // Try team/sprint field from customfield_10010 first
  if (initiative.customFields?.teamSprint) {
    return String(initiative.customFields.teamSprint);
  }

  // Try sprint field as fallback
  if (initiative.customFields?.sprintField) {
    return String(initiative.customFields.sprintField);
  }

  // Try started at sprint as fallback
  if (initiative.customFields?.startedAtSprint) {
    return String(initiative.customFields.startedAtSprint);
  }

  return 'No Team/Sprint';
}

/**
 * Extract theme from initiative
 */
function extractTheme(initiative: Initiative): string {
  // Try custom theme field first
  if (initiative.customFields.theme) {
    return String(initiative.customFields.theme);
  }

  // Try labels
  if (initiative.labels && initiative.labels.length > 0) {
    return initiative.labels[0];
  }

  // Try components
  if (initiative.components && initiative.components.length > 0) {
    return initiative.components[0].name;
  }

  return 'No Theme';
}

/**
 * Extract started at sprint
 */
function extractStartedAtSprint(initiative: Initiative): string {
  if (initiative.customFields.startedAtSprint) {
    return String(initiative.customFields.startedAtSprint);
  }
  return '';
}

/**
 * Extract ends at sprint
 */
function extractEndsAtSprint(initiative: Initiative): string {
  if (initiative.customFields.endsAtSprint) {
    return String(initiative.customFields.endsAtSprint);
  }
  return '';
}

/**
 * Extract description text
 */
function extractDescription(description: any): string {
  if (!description) return '';
  
  // Handle Atlassian Document Format (ADF)
  if (description.content && Array.isArray(description.content)) {
    return extractTextFromADF(description);
  }
  
  // Handle plain text
  if (typeof description === 'string') {
    return description;
  }
  
  return '';
}

/**
 * Extract text from Atlassian Document Format
 * Preserves line breaks from hardBreak nodes and paragraph separations
 */
function extractTextFromADF(adf: any): string {
  if (!adf.content) return '';

  const extractTextRecursive = (node: any): string => {
    let text = '';

    // Handle text nodes
    if (node.text) {
      text += node.text;
    }

    // Handle hard breaks (line breaks within paragraphs)
    if (node.type === 'hardBreak') {
      text += '\n';
    }

    // Handle content array
    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((item: any) => {
        text += extractTextRecursive(item);
      });
    }

    return text;
  };

  let result = '';

  // Handle content array at root level
  if (Array.isArray(adf.content)) {
    adf.content.forEach((item: any) => {
      result += extractTextRecursive(item);
      // Add line break after paragraphs
      if (item.type === 'paragraph') {
        result += '\n';
      }
    });
  } else {
    result = extractTextRecursive(adf);
  }

  return result.trim();
}

/**
 * Calculate start and end dates from sprint numbers using the same logic as backend
 */
function calculateSprintDates(startSprintNumber: number, endSprintNumber: number): {
  startDate: string | null;
  endDate: string | null;
} {
  if (!startSprintNumber || !endSprintNumber) {
    return { startDate: null, endDate: null };
  }

  // Base sprint information: Sprint 123 starts on 7-Feb-2023
  const baseSprint = {
    number: 123,
    startDate: new Date('2023-02-07'),
    endDate: new Date('2023-02-20')
  };

  const sprintDurationDays = 14; // 2 weeks

  // Calculate start date
  const startSprintDifference = startSprintNumber - baseSprint.number;
  const startDaysDifference = startSprintDifference * sprintDurationDays;
  const startDate = new Date(baseSprint.startDate);
  startDate.setDate(startDate.getDate() + startDaysDifference);

  // Calculate end date
  const endSprintDifference = endSprintNumber - baseSprint.number;
  const endDaysDifference = endSprintDifference * sprintDurationDays;
  const endDate = new Date(baseSprint.endDate);
  endDate.setDate(endDate.getDate() + endDaysDifference);

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
}

/**
 * Calculate start and end dates from sprint information (legacy function)
 * This is a placeholder - in reality, you might want to call Jira API to get sprint dates
 */
function calculateDatesFromSprints(startSprint: string, endSprint: string): {
  startDate: string | null;
  endDate: string | null;
} {
  // Use the new sprint calculation if we have valid sprint numbers
  const startSprintNum = parseInt(startSprint, 10);
  const endSprintNum = parseInt(endSprint, 10);

  if (!isNaN(startSprintNum) && !isNaN(endSprintNum)) {
    return calculateSprintDates(startSprintNum, endSprintNum);
  }

  return { startDate: null, endDate: null };
}

/**
 * Get unique values from processed initiatives for filtering
 */
export function getFilterOptions(initiatives: ProcessedInitiative[]) {
  return {
    statuses: getUniqueValues(initiatives, 'status'),
    teams: getUniqueValues(initiatives, 'teamName'),
    themes: getUniqueValues(initiatives, 'theme'),
    assignees: getUniqueValues(initiatives, 'assignee'),
    priorities: getUniqueValues(initiatives, 'priority'),
    sprints: getUniqueValues(initiatives, 'sprint')
  };
}

/**
 * Get unique non-empty values from an array of objects
 */
function getUniqueValues<T>(items: T[], property: keyof T): string[] {
  return [...new Set(items.map(item => String(item[property])))]
    .filter(value => value && value.trim() !== '' && value !== 'Unknown Team')
    .sort();
}
